import { useState, useEffect } from 'react';
import { useAuthStore } from '../store';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { AlertTriangle, Copy, MessageCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import PaymentSuccessAnimation from './PaymentSuccessAnimation';

export default function RenewalModal() {
  const { api, user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);        // /my-subscription/status
  const [paymentData, setPaymentData] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [obrigatorio, setObrigatorio] = useState(false); // vencido = não dá pra fechar

  useEffect(() => { if (user && user.role !== 'admin') checar(); }, [user]);

  const checar = async () => {
    try {
      const r = await api.get('/my-subscription/status');
      const st = r.data;
      if (!st.aplicavel) return;
      setStatus(st);

      // vencido (dias < 0) → modal obrigatório sempre
      if (st.dias < 0) { setObrigatorio(true); setOpen(true); return; }

      // marcos de aviso: 3, 2, 1, 0 dias — 1x por mês por marco
      if ([3, 2, 1, 0].includes(st.dias)) {
        const mesAtual = new Date().toISOString().slice(0, 7); // AAAA-MM
        const chave = `nexu_venc_aviso_${user.id}_${st.dias}_${mesAtual}`;
        if (!localStorage.getItem(chave)) {
          localStorage.setItem(chave, '1');
          setObrigatorio(false);
          setOpen(true);
        }
      }
    } catch { /* silencioso */ }
  };

  const handleRenew = async () => {
    try {
      const response = await api.post(`/renew/${user.id}`);
      setPaymentData(response.data);
      startPaymentPolling(response.data.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar pagamento');
    }
  };

  const startPaymentPolling = (txId) => {
    setCheckingPayment(true);
    const iv = setInterval(async () => {
      try {
        const r = await api.get(`/transactions/${txId}`);
        if (r.data.status === 'approved') {
          clearInterval(iv);
          setCheckingPayment(false);
          setShowSuccess(true);
          setTimeout(() => window.location.reload(), 2500);
        }
      } catch {}
    }, 4000);
    setTimeout(() => clearInterval(iv), 15 * 60 * 1000);
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copiado!'); };

  const abrirContato = () => {
    const num = (status?.contato_upline || '').replace(/\D/g, '');
    if (!num) { toast.error('Contato do seu revendedor não disponível'); return; }
    const msg = encodeURIComponent(`Olá! Quero renovar minha assinatura NexuZap.`);
    window.open(`https://wa.me/${num}?text=${msg}`, '_blank');
  };

  if (showSuccess) return <PaymentSuccessAnimation onComplete={() => setShowSuccess(false)} />;
  if (!status) return null;

  const tituloDias = status.dias < 0 ? 'Sua assinatura venceu' :
    status.dias === 0 ? 'Sua assinatura vence hoje' :
    `Sua assinatura vence em ${status.dias} dia${status.dias > 1 ? 's' : ''}`;

  return (
    <Dialog open={open} onOpenChange={(val) => { if (!obrigatorio && !paymentData) setOpen(val); }}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className={`flex items-center gap-2 ${status.dias < 0 ? 'text-red-500' : 'text-amber-500'}`}>
            {status.dias < 0 ? <AlertTriangle className="w-5 h-5" /> : <Clock className="w-5 h-5" />}
            {tituloDias}
          </DialogTitle>
        </DialogHeader>

        {!paymentData ? (
          <div className="space-y-4">
            <div className={`rounded-lg p-4 border ${status.dias < 0 ? 'bg-red-500/10 border-red-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
              <p className="text-sm">
                {status.dias < 0
                  ? 'Sua conta expirou. Renove para continuar usando o sistema.'
                  : 'Renove sua assinatura para não perder o acesso.'}
              </p>
            </div>

            {status.pode_renovar ? (
              <Button onClick={handleRenew} className="w-full" size="lg">
                Renovar agora{status.valor ? ` — R$ ${Number(status.valor).toFixed(2).replace('.', ',')}` : ''}
              </Button>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground text-center">
                  {status.upline_tem_gateway === false
                    ? 'Seu revendedor não tem pagamento automático no momento.'
                    : 'Seu revendedor está temporariamente sem créditos para renovar.'}
                  {' '}Fale com ele para renovar:
                </p>
                <Button onClick={abrirContato} className="w-full bg-green-600 hover:bg-green-700" size="lg" disabled={!status.contato_upline}>
                  <MessageCircle className="w-4 h-4 mr-2" />
                  {status.contato_upline ? `Falar com ${status.upline_nome || 'meu revendedor'}` : 'Contato indisponível'}
                </Button>
              </div>
            )}
            {!obrigatorio && (
              <button onClick={() => setOpen(false)} className="w-full text-xs text-muted-foreground hover:text-foreground">Agora não</button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {checkingPayment && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-sm text-blue-500">Aguardando confirmação do pagamento...</p>
              </div>
            )}
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">Escaneie o QR Code:</p>
              <img
                src={paymentData.qr_code ? (paymentData.qr_code.startsWith('http') ? paymentData.qr_code : paymentData.qr_code.startsWith('data:') ? paymentData.qr_code : `data:image/png;base64,${paymentData.qr_code}`) : (paymentData.qr_code_url || (paymentData.qr_code_text ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(paymentData.qr_code_text)}` : ''))}
                alt="QR Code PIX"
                className="mx-auto w-56 h-56 border rounded-lg bg-white p-2"
              />
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Ou copie o código PIX:</Label>
              <div className="flex gap-2 mt-2">
                <input type="text" value={paymentData.qr_code_text} readOnly className="flex-1 p-2 text-xs border rounded bg-muted font-mono" />
                <Button size="sm" onClick={() => copyToClipboard(paymentData.qr_code_text)}><Copy className="w-4 h-4" /></Button>
              </div>
            </div>
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
              <p className="text-xs text-yellow-600">⏱️ Após o pagamento, a confirmação pode levar até 2 minutos.</p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
